import { supabase, isSupabaseConfigured } from '../../supabase';
import { Node, Edge } from '@xyflow/react';

export interface AutomationCreateParams {
  name: string;
  trigger: {
    type: string; // 'new_lead', 'status_change', 'sms_received', etc.
    config?: Record<string, unknown>; // e.g., { status: 'closed-won' }
  };
  actions: Array<{
    type: string; // 'send_sms', 'send_email', 'add_task', 'notify', 'delay', 'update_status'
    config?: Record<string, unknown>; // e.g., { message: 'Hello {{name}}' }
  }>;
  conditions?: Array<{
    field: string;
    operator: string;
    value: string;
  }>;
}

/**
 * Creates an automation workflow from structured parameters and saves to Supabase.
 */
export async function createAutomation(params: AutomationCreateParams): Promise<{ success: boolean; message: string; automationId?: string }> {
  if (!isSupabaseConfigured || !supabase) {
    return { success: false, message: 'Supabase is not configured.' };
  }

  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, message: 'Not authenticated.' };

    const nodes: Node[] = [];
    const edges: Edge[] = [];
    let yPos = 50;

    // 1. Trigger node
    const triggerId = `trigger-${Date.now()}`;
    nodes.push({
      id: triggerId,
      type: 'automation',
      position: { x: 400, y: yPos },
      data: {
        label: getTriggerLabel(params.trigger.type),
        type: 'trigger',
        triggerType: params.trigger.type,
        ...params.trigger.config,
      },
    });
    let prevId = triggerId;
    yPos += 150;

    // 2. Condition nodes (if any)
    if (params.conditions && params.conditions.length > 0) {
      for (const condition of params.conditions) {
        const condId = `condition-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        nodes.push({
          id: condId,
          type: 'automation',
          position: { x: 400, y: yPos },
          data: {
            label: `If ${condition.field} ${condition.operator} ${condition.value}`,
            type: 'condition',
            conditionField: condition.field,
            conditionOperator: condition.operator,
            conditionValue: condition.value,
            description: `Checks if ${condition.field} ${condition.operator} ${condition.value}`,
          },
        });
        edges.push({
          id: `e-${prevId}-${condId}`,
          source: prevId,
          target: condId,
          animated: true,
          style: { stroke: 'var(--t-primary)' },
        });
        prevId = condId;
        yPos += 150;
      }
    }

    // 3. Action nodes
    for (const action of params.actions) {
      const actionId = `action-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      nodes.push({
        id: actionId,
        type: 'automation',
        position: { x: 400, y: yPos },
        data: {
          label: getActionLabel(action.type),
          type: action.type === 'delay' ? 'delay' : 'action',
          actionType: action.type,
          ...action.config,
        },
      });
      edges.push({
        id: `e-${prevId}-${actionId}`,
        source: prevId,
        target: actionId,
        animated: true,
        style: { stroke: 'var(--t-primary)' },
      });
      prevId = actionId;
      yPos += 150;
    }

    // 4. Save to Supabase
    const { data, error } = await supabase
      .from('user_automations')
      .insert([{
        user_id: user.id,
        name: params.name,
        nodes,
        edges,
        is_active: true,
        updated_at: new Date().toISOString(),
      }])
      .select()
      .single();

    if (error) throw error;

    return {
      success: true,
      message: `Automation "${params.name}" created and activated successfully.`,
      automationId: data?.id,
    };
  } catch (err) {
    console.error('[automationTools] createAutomation error:', err);
    return { success: false, message: `Failed to create automation: ${err instanceof Error ? err.message : 'Unknown error'}` };
  }
}

/**
 * Lists all automations for the current user.
 */
export async function listAutomations(): Promise<{ success: boolean; message: string; automations?: Array<{ id: string; name: string; is_active: boolean; run_count: number; last_triggered_at: string | null }> }> {
  if (!isSupabaseConfigured || !supabase) {
    return { success: false, message: 'Supabase is not configured.' };
  }

  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, message: 'Not authenticated.' };

    const { data, error } = await supabase
      .from('user_automations')
      .select('id, name, is_active, run_count, last_triggered_at')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false });

    if (error) throw error;

    const automations = (data || []).map(a => ({
      id: a.id,
      name: a.name,
      is_active: a.is_active,
      run_count: a.run_count || 0,
      last_triggered_at: a.last_triggered_at,
    }));

    if (automations.length === 0) {
      return { success: true, message: 'You have no automations yet. You can create one by saying "Create an automation..." or by using the Automations Hub.', automations: [] };
    }

    const summary = automations.map((a, i) => 
      `${i + 1}. **${a.name}** — ${a.is_active ? '🟢 Active' : '🔴 Paused'} (${a.run_count} runs)`
    ).join('\n');

    return { success: true, message: `You have ${automations.length} automation(s):\n\n${summary}`, automations };
  } catch (err) {
    console.error('[automationTools] listAutomations error:', err);
    return { success: false, message: 'Failed to fetch automations.' };
  }
}

/**
 * Toggle an automation on/off by name or ID.
 */
export async function toggleAutomation(nameOrId: string, enabled: boolean): Promise<{ success: boolean; message: string }> {
  if (!isSupabaseConfigured || !supabase) {
    return { success: false, message: 'Supabase is not configured.' };
  }

  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, message: 'Not authenticated.' };

    // Try to find by name first (case-insensitive)
    const { data: automations } = await supabase
      .from('user_automations')
      .select('id, name')
      .eq('user_id', user.id);

    const match = (automations || []).find(a =>
      a.id === nameOrId || a.name.toLowerCase().includes(nameOrId.toLowerCase())
    );

    if (!match) return { success: false, message: `No automation found matching "${nameOrId}".` };

    const { error } = await supabase
      .from('user_automations')
      .update({ is_active: enabled, updated_at: new Date().toISOString() })
      .eq('id', match.id);

    if (error) throw error;

    return { success: true, message: `Automation "${match.name}" is now ${enabled ? '🟢 enabled' : '🔴 disabled'}.` };
  } catch (err) {
    console.error('[automationTools] toggleAutomation error:', err);
    return { success: false, message: 'Failed to toggle automation.' };
  }
}

/**
 * Delete an automation by name or ID.
 */
export async function deleteAutomation(nameOrId: string): Promise<{ success: boolean; message: string }> {
  if (!isSupabaseConfigured || !supabase) {
    return { success: false, message: 'Supabase is not configured.' };
  }

  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, message: 'Not authenticated.' };

    const { data: automations } = await supabase
      .from('user_automations')
      .select('id, name')
      .eq('user_id', user.id);

    const match = (automations || []).find(a =>
      a.id === nameOrId || a.name.toLowerCase().includes(nameOrId.toLowerCase())
    );

    if (!match) return { success: false, message: `No automation found matching "${nameOrId}".` };

    const { error } = await supabase
      .from('user_automations')
      .delete()
      .eq('id', match.id);

    if (error) throw error;

    return { success: true, message: `Automation "${match.name}" has been deleted.` };
  } catch (err) {
    console.error('[automationTools] deleteAutomation error:', err);
    return { success: false, message: 'Failed to delete automation.' };
  }
}

// --- Helpers ---

function getTriggerLabel(type: string): string {
  const map: Record<string, string> = {
    new_lead: 'New Lead Created',
    status_change: 'Lead Status Changed',
    high_score: 'High Lead Score',
    sms_received: 'SMS Received',
    task_overdue: 'Task Overdue',
    birthday: 'Client Birthday',
    referral_request: 'Referral Request',
    offer_submitted: 'Offer Submitted',
    monthly_run: '1st of the Month',
    inactivity: 'Lead Inactive',
  };
  return map[type] || type;
}

function getActionLabel(type: string): string {
  const map: Record<string, string> = {
    send_sms: 'Send SMS',
    send_email: 'Send Email',
    notify: 'In-App Notification',
    add_task: 'Create Task',
    delay: 'Wait / Delay',
    update_status: 'Update Lead Status',
    send_chat: 'Post to Chat',
  };
  return map[type] || type;
}
